import { useTranslation } from 'react-i18next';
import { PRESENCE_TOKENS, type PresenceShape, type PresenceState } from './tokens';

interface Props {
  state: PresenceState;
  /** Whose status this is. Included in the accessible name when given. */
  name?: string;
  /** Diameter in pixels. */
  size?: number;
  /** Show the label as text beside the badge, not only to screen readers. */
  showLabel?: boolean;
  /** Theme surface the badge sits on. */
  theme?: 'light' | 'dark';
}

/**
 * The status badge shown beside a person's name (FR-PRES-01/02).
 *
 * It carries its meaning three ways: colour, a shape that differs per state,
 * and a text label exposed as the accessible name and tooltip. Status changes
 * of other people are deliberately not announced to screen readers — that
 * would be a stream of interruptions about things the user did not ask about.
 */
export function PresenceBadge({ state, name, size = 12, showLabel = false, theme = 'light' }: Props) {
  const { t } = useTranslation('collaboration');
  const token = PRESENCE_TOKENS[state];
  const status = t(token.labelKey);
  const label = name ? t('presence.withName', { name, status }) : status;
  const colour = token[theme];

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      title={label}
    >
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox="0 0 12 12"
        data-shape={token.shape}
        data-state={state}
      >
        <Shape shape={token.shape} colour={colour} />
      </svg>
      {showLabel && <span aria-hidden="true">{status}</span>}
    </span>
  );
}

function Shape({ shape, colour }: { shape: PresenceShape; colour: string }) {
  switch (shape) {
    case 'filled':
      return <circle cx="6" cy="6" r="5" fill={colour} />;
    case 'ring':
      return <circle cx="6" cy="6" r="4.25" fill="none" stroke={colour} strokeWidth="1.5" />;
    case 'clock':
      return (
        <>
          <circle cx="6" cy="6" r="5" fill={colour} />
          <path d="M6 3.2V6.2L8 7.4" stroke="#FFFFFF" strokeWidth="1.3" fill="none" strokeLinecap="round" />
        </>
      );
    case 'bar':
      return (
        <>
          <circle cx="6" cy="6" r="5" fill={colour} />
          <rect x="3" y="5.25" width="6" height="1.5" rx="0.75" fill="#FFFFFF" />
        </>
      );
    case 'arrow':
      return (
        <>
          <circle cx="6" cy="6" r="5" fill={colour} />
          <path d="M4 6H8M6.4 4.4L8 6L6.4 7.6" stroke="#FFFFFF" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
  }
}
